import { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  DocumentData,
  onSnapshot,
  query,
  QuerySnapshot,
  Timestamp,
  where,
} from "firebase/firestore";
import { Badge, Group, Text, Tooltip } from "@mantine/core";
import { Link } from "react-router-dom";
import {
  IconBriefcase,
  IconChartLine,
  IconFileText,
  IconShoppingCart,
  IconUserShield,
} from "@tabler/icons-react";
import { useFirestore } from "../../helpers/firebaseContext";
import { KYC, Order, Service, ServiceType } from "../../types";

/**
 * "What is this KYC for?"
 *
 * A KYC is submitted for something: a fund order, a KFH Trade / brokerage /
 * portfolio service, sometimes for a minor's account through the guardian, or
 * for nothing in particular (a general customer KYC). This module resolves
 * that purpose for every KYC of a customer so the KYC page can show it next
 * to the version dropdown and in the dropdown itself.
 *
 * Sources, in priority order:
 *  1. Products that point at the KYC (`product.kyc_id === kyc.id`):
 *     - the customer's own orders / services
 *     - minors' orders / services whose `guardianCustomerID` is this customer
 *       (a guardian's KYC is what the minor's product is submitted against)
 *  2. The KYC's own `completedKYCData.productRef` (contract-flow KYCs).
 *  3. The link the KYC came from: a contract link carrying `kyc_id`, or a
 *     kycupdatelinks doc matching `kyc.linkID`.
 *  4. Otherwise it is a general KYC.
 */

export type KycPurposeKind = "order" | "service" | "kyc";

export interface KycPurposeItem {
  kind: KycPurposeKind;
  /** e.g. "Subscription order", "KFH Trade service", "General KYC" */
  label: string;
  /** e.g. fund name, or the minor's name */
  detail?: string;
  /** Route to open the product, when there is one. */
  path?: string;
  /** The product / KYC is for a minor's account. */
  forMinor: boolean;
  /** Product document id (or link id for link-derived items). */
  id?: string;
  /** Product status title, when known. */
  status?: string;
  /** Where this item was derived from — shown in the tooltip. */
  source: "product" | "kyc" | "link";
}

interface LinkedProduct {
  kind: "order" | "service";
  id: string;
  kycID?: string;
  /** Firestore customer doc id the product lives under. */
  customerID?: string;
  customerName?: string;
  guardianCustomerID?: string;
  serviceType?: ServiceType;
  orderType?: string;
  fundName?: string;
  status?: string;
  created?: Timestamp;
}

const SERVICE_LABELS: Record<ServiceType, string> = {
  kfhtrade: "KFH Trade service",
  kfhbrokerage: "KFH Brokerage service",
  portfolio: "Portfolio service",
  ipo: "IPO service",
};

export const serviceLabel = (type?: string): string =>
  (type && SERVICE_LABELS[type as ServiceType]) || (type ? `${type} service` : "Service");

export const servicePath = (id: string, type?: string): string =>
  type === "portfolio" ? `/portfolios/${id}` : `/services/${id}`;

const orderLabel = (type?: string): string =>
  type === "redemption" ? "Redemption order" : "Subscription order";

const toLinkedProduct = (kind: "order" | "service", snap: QuerySnapshot<DocumentData>): LinkedProduct[] =>
  snap.docs.map((d) => {
    const data = d.data() as Partial<Order> & Partial<Service>;
    return {
      kind,
      id: d.id,
      kycID: data.kyc_id,
      customerID: d.ref.parent.parent?.id,
      customerName: data.customerObj?.name_en,
      guardianCustomerID: data.guardianCustomerID,
      serviceType: data.serviceType,
      orderType: data.type,
      fundName: data.fund?.name_en ?? data.fund?.name,
      status: kind === "order" ? data.tags?.orderStatus?.title : data.tags?.serviceStatus?.title,
      created: data.created,
    };
  });

/**
 * Every order / service that could be tied to one of this customer's KYCs,
 * grouped by kyc_id. Includes products owned by minors this customer is the
 * guardian of, since those are submitted against the guardian's KYC.
 */
export const useKycProducts = (customerID?: string): Map<string, LinkedProduct[]> => {
  const db = useFirestore();
  const [own, setOwn] = useState<LinkedProduct[]>([]);
  const [ownServices, setOwnServices] = useState<LinkedProduct[]>([]);
  const [minorOrders, setMinorOrders] = useState<LinkedProduct[]>([]);
  const [minorServices, setMinorServices] = useState<LinkedProduct[]>([]);

  useEffect(() => {
    setOwn([]);
    setOwnServices([]);
    setMinorOrders([]);
    setMinorServices([]);
    if (!customerID) return;

    const ignore = (what: string) => (e: unknown) => console.warn(`[KycPurpose] ${what} unavailable`, e);

    const unsubs = [
      onSnapshot(collection(db, "customers", customerID, "orders"),
        (s) => setOwn(toLinkedProduct("order", s)), ignore("own orders")),
      onSnapshot(collection(db, "customers", customerID, "services"),
        (s) => setOwnServices(toLinkedProduct("service", s)), ignore("own services")),
      // Minors' products live under the minor's customer doc and point back at
      // the guardian through guardianCustomerID (COLLECTION_GROUP index exists).
      onSnapshot(query(collectionGroup(db, "orders"), where("guardianCustomerID", "==", customerID)),
        (s) => setMinorOrders(toLinkedProduct("order", s)), ignore("minors' orders")),
      onSnapshot(query(collectionGroup(db, "services"), where("guardianCustomerID", "==", customerID)),
        (s) => setMinorServices(toLinkedProduct("service", s)), ignore("minors' services")),
    ];
    return () => unsubs.forEach((u) => u());
  }, [db, customerID]);

  return useMemo(() => {
    const byKyc = new Map<string, LinkedProduct[]>();
    const seen = new Set<string>();
    for (const p of [...own, ...ownServices, ...minorOrders, ...minorServices]) {
      if (!p.kycID) continue;
      const key = `${p.kind}:${p.customerID}:${p.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const list = byKyc.get(p.kycID) ?? [];
      list.push(p);
      byKyc.set(p.kycID, list);
    }
    byKyc.forEach((list) => list.sort((a, b) => (b.created?.seconds ?? 0) - (a.created?.seconds ?? 0)));
    return byKyc;
  }, [own, ownServices, minorOrders, minorServices]);
};

/** "customers/x/services/abc" or "services/abc" → { kind, id } */
const parseProductRef = (ref?: string): { kind: "order" | "service"; id: string } | undefined => {
  if (!ref) return undefined;
  const parts = ref.split("/").filter(Boolean);
  const collectionName = parts[parts.length - 2];
  const id = parts[parts.length - 1];
  if (!id) return undefined;
  if (collectionName === "orders") return { kind: "order", id };
  if (collectionName === "services") return { kind: "service", id };
  return undefined;
};

/** Minimal shape of the link rows the KYC page already loads (kycupdatelinks + contract_links). */
export interface KycLinkLike {
  id?: string;
  is_contract?: boolean;
  kyc_id?: string;
  updateType?: string;
  /** kycupdatelinks: the KYC doc being rewritten by this update link. */
  updateKycID?: string;
  orderID?: string;
  serviceID?: string;
  serviceInfo?: Partial<Service>;
  orderInfo?: Partial<Order>;
  completedKYCData?: { productRef?: string; productTitle?: string; customerId?: string };
  prefillData?: { accountType?: string };
}

export interface DescribeKycArgs {
  kyc: (Partial<KYC> & { id?: string }) | undefined;
  /** Firestore id of the customer whose KYC page is open. */
  customerID?: string;
  productsByKyc: Map<string, LinkedProduct[]>;
  links?: KycLinkLike[];
  /** Review trail of this KYC (private/actions.approvals). */
  actions?: { type?: string }[];
  /** The customer has a KYC created before this one, so this one is a newer version. */
  olderKycExists?: boolean;
}

/**
 * A KYC that is not tied to any order or service. Says whether it is the
 * customer's first KYC, a resubmission after CR sent it back, a periodic
 * renewal, or a document-only upload.
 */
const describeStandaloneKyc = (
  kyc: Partial<KYC>,
  link: KycLinkLike | undefined,
  actions: { type?: string }[],
  olderKycExists: boolean | undefined,
  forMinor: boolean,
  source: KycPurposeItem["source"],
): KycPurposeItem => {
  const minorPrefix = forMinor ? (kyc.guardianCustomerID ? "Minor account " : "Guardian ") : "";
  const sentBack = actions.some((a) => a.type === "needs_update" || a.type === "needs_agent_update")
    || link?.updateType === "needs_update" || link?.updateType === "needs_agent_update";
  const submits = actions.filter((a) => a.type === "submit").length;
  const isUpdate = sentBack || submits > 1 || kyc.kyc_update === true || !!link?.updateKycID;

  if (link?.updateType === "upload_docs" && !isUpdate) {
    return { kind: "kyc", id: link.id, label: `${minorPrefix}Document upload`, detail: "documents requested by CR, no product", forMinor, source };
  }
  if (isUpdate) {
    return {
      kind: "kyc", id: link?.id,
      label: `${minorPrefix}KYC update`,
      detail: sentBack ? "resubmitted after CR sent it back, not tied to a product" : "customer updated their KYC, not tied to a product",
      forMinor, source,
    };
  }
  if (olderKycExists) {
    return { kind: "kyc", id: link?.id, label: `${minorPrefix}KYC renewal`, detail: "new KYC version, not tied to a product", forMinor, source };
  }
  if (olderKycExists === false) {
    return { kind: "kyc", id: link?.id, label: `${minorPrefix}Initial KYC`, detail: "customer's first KYC, not tied to a product", forMinor, source };
  }
  return { kind: "kyc", id: link?.id, label: `${minorPrefix}General KYC`, detail: "not tied to an order or service", forMinor, source };
};

export const describeKycPurpose = ({ kyc, customerID, productsByKyc, links = [], actions = [], olderKycExists }: DescribeKycArgs): KycPurposeItem[] => {
  if (!kyc?.id) return [];
  const items: KycPurposeItem[] = [];

  // This KYC document is a minor's mirror of the guardian's KYC.
  const isMinorMirror = !!(kyc.guardianCustomerID && kyc.guardianKycID);
  // This is a guardian's KYC filled in for a minor (beneficiary is the child).
  const isGuardianForMinor = !!kyc.tradeForBeneficiaries && !!kyc.beneficiaryInformation?.[0]?.beneficiary_civil_id;

  // 1. products pointing at this KYC
  for (const p of productsByKyc.get(kyc.id) ?? []) {
    const ownedByOther = !!customerID && !!p.customerID && p.customerID !== customerID;
    const forMinor = !!p.guardianCustomerID || isMinorMirror;
    const minorName = ownedByOther ? p.customerName : undefined;
    items.push({
      kind: p.kind,
      id: p.id,
      label: p.kind === "order" ? orderLabel(p.orderType) : serviceLabel(p.serviceType),
      detail: [p.kind === "order" ? p.fundName : undefined, minorName ? `minor: ${minorName}` : undefined]
        .filter(Boolean).join(" · ") || undefined,
      path: p.kind === "order" ? `/orders/${p.id}` : servicePath(p.id, p.serviceType),
      forMinor,
      status: p.status,
      source: "product",
    });
  }
  if (items.length) return items;

  // 2. the KYC's own contract data
  const ownRef = parseProductRef(kyc.completedKYCData?.productRef);
  if (ownRef) {
    const forMinor = isMinorMirror || isGuardianForMinor
      || (!!kyc.completedKYCData?.customerId && !!customerID && kyc.completedKYCData.customerId !== customerID);
    items.push({
      kind: ownRef.kind,
      id: ownRef.id,
      label: ownRef.kind === "order" ? "Order" : (kyc.completedKYCData?.productTitle || "Service"),
      path: ownRef.kind === "order" ? `/orders/${ownRef.id}` : `/services/${ownRef.id}`,
      forMinor,
      source: "kyc",
    });
    return items;
  }

  // 3. the link the KYC was submitted through
  const link = links.find((l) => (l.is_contract && l.kyc_id === kyc.id) || (!l.is_contract && !!kyc.linkID && l.id === kyc.linkID));
  if (link) {
    const forMinor = isMinorMirror || isGuardianForMinor || link.prefillData?.accountType === "minor";
    const linkRef = parseProductRef(link.completedKYCData?.productRef);
    if (linkRef) {
      items.push({
        kind: linkRef.kind,
        id: linkRef.id,
        label: linkRef.kind === "order" ? "Order" : (link.completedKYCData?.productTitle || "Service"),
        path: linkRef.kind === "order" ? `/orders/${linkRef.id}` : `/services/${linkRef.id}`,
        forMinor,
        source: "link",
      });
    } else if (link.orderID) {
      items.push({
        kind: "order", id: link.orderID, label: orderLabel(link.orderInfo?.type), detail: link.orderInfo?.fund?.name_en,
        path: `/orders/${link.orderID}`, forMinor, source: "link",
      });
    } else if (link.serviceID) {
      items.push({
        kind: "service", id: link.serviceID, label: serviceLabel(link.serviceInfo?.serviceType),
        path: servicePath(link.serviceID, link.serviceInfo?.serviceType), forMinor, source: "link",
      });
    } else {
      items.push(describeStandaloneKyc(kyc, link, actions, olderKycExists, forMinor, "link"));
    }
    return items;
  }

  // 4. nothing points at it: a plain KYC (initial, update, renewal)
  items.push(describeStandaloneKyc(kyc, undefined, actions, olderKycExists, isMinorMirror || isGuardianForMinor, "kyc"));
  return items;
};

/** One-line summary for dropdown labels, e.g. "KFH Trade service (minor), Subscription order" */
export const summarizeKycPurpose = (items: KycPurposeItem[]): string => {
  const parts: string[] = [];
  for (const item of items) {
    const text = `${item.label}${item.forMinor ? " (minor)" : ""}`;
    if (!parts.includes(text)) parts.push(text);
  }
  return parts.join(", ");
};

const kindIcon = (item: KycPurposeItem) => {
  if (item.kind === "order") return <IconShoppingCart size={12} />;
  if (item.kind === "service") return item.label.toLowerCase().includes("portfolio") ? <IconBriefcase size={12} /> : <IconChartLine size={12} />;
  return <IconFileText size={12} />;
};

const kindColor = (item: KycPurposeItem) => {
  if (item.kind === "order") return "blue";
  if (item.kind === "service") return "teal";
  return "gray";
};

export const KycPurposeBadges = ({ items, compact = false }: { items: KycPurposeItem[]; compact?: boolean }) => {
  if (!items.length) return null;
  return (
    <Group gap={6} wrap="wrap" align="center">
      {!compact && <Text size="sm" fw={600} c="dimmed">KYC for:</Text>}
      {items.map((item, idx) => {
        const tooltip = [
          item.detail,
          item.id ? `ID: ${item.id}` : undefined,
          item.status ? `Status: ${item.status}` : undefined,
          item.source === "product" ? "Linked through the product's kyc_id"
            : item.source === "link" ? "Derived from the KYC link" : "Derived from the KYC itself",
        ].filter(Boolean).join(" · ");
        const badgeProps = {
          size: compact ? "xs" : "sm",
          variant: item.kind === "kyc" ? "outline" : "light",
          color: kindColor(item),
          leftSection: kindIcon(item),
          rightSection: item.forMinor ? <IconUserShield size={12} /> : undefined,
        } as const;
        const text = `${item.label}${item.forMinor ? " · minor" : ""}`;
        const badge = item.path ? (
          <Badge {...badgeProps} component={Link} to={item.path} style={{ cursor: "pointer" }}>
            {text}
          </Badge>
        ) : (
          <Badge {...badgeProps}>{text}</Badge>
        );
        return (
          <Tooltip key={`${item.kind}-${item.id ?? idx}`} label={tooltip} withArrow multiline maw={320}>
            {badge}
          </Tooltip>
        );
      })}
    </Group>
  );
};
